'use client';

import { useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import {
  Eye, EyeOff, Upload, Check, ArrowRight, ArrowLeft,
  Rocket, Lock, CreditCard, Settings2, MapPin, Truck, Camera, Bell, Shield,
  FileBarChart, Cpu, Brain, Users, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  useSignupWizard,
  industries,
  fleetSizes,
  countries,
  colorPresets,
  serviceModules,
  getSlugFromName,
} from '@/stores/signupWizardStore';
import { pricingTiers } from '@/components/marketing/pricing-card';
import { fetchPublicBillingPlans, type BillingPlan } from '@/lib/api';
import {
  estimateCommercialCycleAmount,
  normalizeCommercialBillingCycle,
  resolveCommercialSelection,
} from '@glonextrack/contracts';
import {
  PACKAGE_REQUIRED_SERVICE_KEYS,
  TENANT_PACKAGE_DESCRIPTIONS,
  TENANT_PACKAGE_LABELS,
  applyIndustryServiceBundle,
  isSameServiceSelection,
  normalizeIndustrySelection,
  resolveTenantPackage,
} from '@/lib/industry-packages';

/* ── Shared ────────────────────────────────────────────────── */

function StepHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="mb-8 text-center">
      <h2 className="text-2xl font-bold text-slate-900 lg:text-3xl">{title}</h2>
      <p className="mt-2 text-sm text-slate-500">{subtitle}</p>
    </div>
  );
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1.5">
      {children}
    </label>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-red-500">{message}</p>;
}

/* ── Step 1: Account ──────────────────────────────────────── */

export function AccountStep() {
  const { account, updateAccount, nextStep } = useSignupWizard();
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!account.firstName.trim()) e.firstName = 'First name is required';
    if (!account.lastName.trim()) e.lastName = 'Last name is required';
    if (!account.email.includes('@')) e.email = 'Valid email is required';
    if (account.password.length < 8) e.password = 'Minimum 8 characters';
    if (account.password !== account.confirmPassword) e.confirmPassword = 'Passwords do not match';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleContinue = () => {
    if (validate()) nextStep();
  };

  const strength = account.password.length >= 12 ? 3 : account.password.length >= 8 ? 2 : account.password.length >= 4 ? 1 : 0;
  const strengthColors = ['bg-slate-200', 'bg-red-400', 'bg-amber-400', 'bg-emerald-500'];
  const strengthLabels = ['', 'Weak', 'Good', 'Strong'];

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <StepHeader title="Create Your Account" subtitle="This will be your administrator login for the platform." />

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="firstName">First Name *</FieldLabel>
          <Input id="firstName" value={account.firstName} onChange={(e) => updateAccount({ firstName: e.target.value })} placeholder="Ada" />
          <FieldError message={errors.firstName} />
        </div>
        <div>
          <FieldLabel htmlFor="lastName">Last Name *</FieldLabel>
          <Input id="lastName" value={account.lastName} onChange={(e) => updateAccount({ lastName: e.target.value })} placeholder="Lovelace" />
          <FieldError message={errors.lastName} />
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="email">Email Address *</FieldLabel>
        <Input id="email" type="email" value={account.email} onChange={(e) => updateAccount({ email: e.target.value })} placeholder="you@company.com" />
        <FieldError message={errors.email} />
      </div>

      <div>
        <FieldLabel htmlFor="phone">Phone Number (optional)</FieldLabel>
        <Input id="phone" type="tel" value={account.phone} onChange={(e) => updateAccount({ phone: e.target.value })} placeholder="+27 10 500 1234" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="password">Password *</FieldLabel>
          <div className="relative">
            <Input id="password" type={showPw ? 'text' : 'password'} value={account.password} onChange={(e) => updateAccount({ password: e.target.value })} placeholder="Min. 8 characters" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {account.password && (
            <div className="mt-2 flex items-center gap-2">
              <div className="flex flex-1 gap-1">
                {[1, 2, 3].map((level) => (
                  <div key={level} className={`h-1 flex-1 rounded-full ${strength >= level ? strengthColors[strength] : 'bg-slate-200'}`} />
                ))}
              </div>
              <span className="text-xs text-slate-500">{strengthLabels[strength]}</span>
            </div>
          )}
          <FieldError message={errors.password} />
        </div>
        <div>
          <FieldLabel htmlFor="confirmPassword">Confirm Password *</FieldLabel>
          <Input id="confirmPassword" type={showPw ? 'text' : 'password'} value={account.confirmPassword} onChange={(e) => updateAccount({ confirmPassword: e.target.value })} placeholder="Re-enter password" />
          <FieldError message={errors.confirmPassword} />
        </div>
      </div>

      <Button variant="accent" size="lg" className="w-full rounded-full" onClick={handleContinue}>
        Continue <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

/* ── Step 2: Organization ─────────────────────────────────── */

export function OrganizationStep() {
  const {
    organization,
    services,
    updateOrganization,
    updateServices,
    nextStep,
    prevStep,
  } = useSignupWizard();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!organization.name.trim()) e.name = 'Organization name is required';
    if (!organization.industry) e.industry = 'Please select an industry';
    if (!organization.country) e.country = 'Please select a country';
    if (!organization.fleetSize) e.fleetSize = 'Please select fleet size';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleContinue = () => { if (validate()) nextStep(); };
  const slug = getSlugFromName(organization.name);
  const handleIndustryChange = (rawIndustry: string) => {
    const canonicalIndustry = normalizeIndustrySelection(rawIndustry);
    const tenantPackage = resolveTenantPackage(canonicalIndustry);
    const nextServices = applyIndustryServiceBundle(
      services.enabledServices,
      canonicalIndustry,
    );
    updateOrganization({ industry: canonicalIndustry, tenantPackage });
    updateServices({ enabledServices: nextServices });
  };

  const packageLabel = TENANT_PACKAGE_LABELS[organization.tenantPackage];
  const packageDescription = TENANT_PACKAGE_DESCRIPTIONS[organization.tenantPackage];
  const packageWalkthrough =
    organization.tenantPackage === 'insurance'
      ? [
          'Plan step aligns insurer pricing with telemetry-heavy workloads.',
          'Services step auto-locks insurance telemetry, UBI, claims, and underwriting modules.',
          'Review step provisions an insurer tenant profile for web and mobile access.',
        ]
      : organization.tenantPackage === 'farming'
        ? [
            'Plan step aligns farm operations volume with device and seat allocations.',
            'Services step auto-locks farming telemetry modules with map and boundary tooling.',
            'Review step provisions a farming tenant profile for web and mobile access.',
          ]
        : [
            'Plan step aligns your baseline fleet package and billing cycle.',
            'Services step configures your selected core tracking modules.',
            'Review step provisions your tenant workspace for launch.',
          ];

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <StepHeader title="Organization Details" subtitle="Tell us about your company to set up your workspace." />

      <div>
        <FieldLabel htmlFor="orgName">Organization Name *</FieldLabel>
        <Input id="orgName" value={organization.name} onChange={(e) => updateOrganization({ name: e.target.value })} placeholder="Your Company Name" />
        {slug && (
          <p className="mt-1 text-xs text-slate-400">
            Your workspace: <span className="font-medium text-emerald-600">{slug}.glonextrack.com</span>
          </p>
        )}
        <FieldError message={errors.name} />
      </div>

      <div>
        <FieldLabel htmlFor="industry">Industry *</FieldLabel>
        <select id="industry" value={organization.industry} onChange={(e) => handleIndustryChange(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100">
          <option value="">Select your industry</option>
          {industries.map((i) => (<option key={i.value} value={i.value}>{i.label}</option>))}
        </select>
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          <p className="font-semibold text-slate-700">Package Bundle: {packageLabel}</p>
          <p className="mt-1">{packageDescription}</p>
          <ol className="mt-2 list-decimal space-y-1 pl-4 text-[11px] text-slate-500">
            {packageWalkthrough.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </div>
        <FieldError message={errors.industry} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <FieldLabel htmlFor="country">Country *</FieldLabel>
          <select id="country" value={organization.country} onChange={(e) => updateOrganization({ country: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100">
            <option value="">Select country</option>
            {countries.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
          </select>
          <FieldError message={errors.country} />
        </div>
        <div>
          <FieldLabel htmlFor="region">Region (optional)</FieldLabel>
          <Input id="region" value={organization.region} onChange={(e) => updateOrganization({ region: e.target.value })} placeholder="e.g. Gauteng" />
        </div>
      </div>

      <div>
        <FieldLabel htmlFor="fleetSize">Fleet Size *</FieldLabel>
        <select id="fleetSize" value={organization.fleetSize} onChange={(e) => updateOrganization({ fleetSize: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100">
          <option value="">Select fleet size</option>
          {fleetSizes.map((f) => (<option key={f.value} value={f.value}>{f.label}</option>))}
        </select>
        <FieldError message={errors.fleetSize} />
      </div>

      <div>
        <FieldLabel htmlFor="website">Website (optional)</FieldLabel>
        <Input id="website" value={organization.website} onChange={(e) => updateOrganization({ website: e.target.value })} placeholder="https://yourcompany.com" />
      </div>

      <div className="flex gap-3">
        <Button variant="outline" size="lg" className="flex-1 rounded-full" onClick={prevStep}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button variant="accent" size="lg" className="flex-1 rounded-full" onClick={handleContinue}>
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ── Step 3: Plan Selection ───────────────────────────────── */

export function PlanStep() {
  const { plan, organization, updatePlan, nextStep, prevStep } = useSignupWizard();
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [planLoadError, setPlanLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadPlans = async () => {
      setLoadingPlans(true);
      setPlanLoadError(null);
      try {
        const data = await fetchPublicBillingPlans();
        if (cancelled) return;

        setPlans(data);

        // If the current selection isn't available anymore, default to first plan.
        if (data.length > 0 && !data.some((p) => p.id === plan.tier)) {
          updatePlan({ tier: data[0].id });
        }
      } catch (err) {
        if (!cancelled) {
          setPlans([]);
          setPlanLoadError(err instanceof Error ? err.message : 'Unable to load plans');
        }
      } finally {
        if (!cancelled) {
          setLoadingPlans(false);
        }
      }
    };

    void loadPlans();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePlan]);

  const commercialPreview = useMemo(() => {
    if (plans.length === 0) return null;
    try {
      return resolveCommercialSelection({
        plans,
        requestedPlanId: plan.tier,
        billingCycle: plan.billingCycle,
        seatCount: plan.seatCount,
        vehicleCount: plan.vehicleCount,
        deviceCount: plan.deviceCount,
        industry: organization.industry,
        tenantPackage: organization.tenantPackage,
      });
    } catch {
      return null;
    }
  }, [
    organization.industry,
    organization.tenantPackage,
    plan.billingCycle,
    plan.deviceCount,
    plan.seatCount,
    plan.tier,
    plan.vehicleCount,
    plans,
  ]);

  useEffect(() => {
    if (!commercialPreview) return;
    if (
      commercialPreview.plan.id !== plan.tier ||
      commercialPreview.billingCycle !== plan.billingCycle
    ) {
      updatePlan({
        tier: commercialPreview.plan.id,
        billingCycle: commercialPreview.billingCycle,
      });
    }
  }, [commercialPreview, plan.billingCycle, plan.tier, updatePlan]);

  const selectedPlan = commercialPreview?.plan ?? plans.find((p) => p.id === plan.tier) ?? plans[0] ?? null;
  const selectedAmountPreview = useMemo(() => {
    if (!commercialPreview) return null;
    return estimateCommercialCycleAmount(
      commercialPreview.plan,
      commercialPreview.billingCycle,
      commercialPreview.counts,
    );
  }, [commercialPreview]);

  const cycles = useMemo(() => {
    if (!selectedPlan) {
      return [];
    }
    const available =
      selectedPlan.availableBillingCycles.length > 0
        ? selectedPlan.availableBillingCycles
        : ['monthly'];
    return available.map((rawCycle) => {
      const cycle = normalizeCommercialBillingCycle(rawCycle);
      return {
        id: cycle,
        label: cycle.charAt(0).toUpperCase() + cycle.slice(1),
        discount: estimateCommercialCycleAmount(selectedPlan, cycle, {
          seatCount: Math.max(1, plan.seatCount),
          vehicleCount: Math.max(0, plan.vehicleCount),
          deviceCount: Math.max(0, plan.deviceCount),
        }).discountPercent,
      };
    });
  }, [plan.deviceCount, plan.seatCount, plan.vehicleCount, selectedPlan]);

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <StepHeader
        title="Choose Your Plan"
        subtitle={`All plans include a 14-day free trial. Package: ${TENANT_PACKAGE_LABELS[organization.tenantPackage]}.`}
      />

      {/* Billing cycle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 shadow-sm">
          {cycles.map((c) => (
            <button key={c.id} onClick={() => updatePlan({ billingCycle: c.id })} className={`rounded-full px-4 py-2 text-sm font-medium transition-all ${plan.billingCycle === c.id ? 'bg-emerald-500 text-white shadow-md' : 'text-slate-600 hover:text-slate-900'}`}>
              {c.label} {c.discount > 0 && <span className="text-xs ml-1 opacity-80">-{c.discount}%</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loadingPlans ? (
          <div className="col-span-full rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            Loading plans...
          </div>
        ) : planLoadError ? (
          <div className="col-span-full rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
            Failed to load plans: {planLoadError}
          </div>
        ) : (
          plans.map((tier) => {
            const isSelected = commercialPreview?.plan.id === tier.id;
            const tierCycle =
              tier.availableBillingCycles.includes(plan.billingCycle)
                ? plan.billingCycle
                : tier.availableBillingCycles[0] ?? 'monthly';
            const price = estimateCommercialCycleAmount(tier, tierCycle, {
              seatCount: Math.max(1, plan.seatCount),
              vehicleCount: Math.max(0, plan.vehicleCount),
              deviceCount: Math.max(0, plan.deviceCount),
            });

            return (
              <button
                key={tier.id}
                onClick={() => updatePlan({ tier: tier.id })}
                className={`text-left rounded-2xl border-2 p-6 transition-all ${isSelected ? 'border-emerald-500 bg-emerald-50/50 shadow-md' : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'}`}
              >
                {tier.tierKey === 'ai' && (
                  <Badge variant="accent" className="mb-2 text-[10px]">
                    Most Popular
                  </Badge>
                )}
                <h3 className="text-lg font-semibold text-slate-900">{tier.name}</h3>
                <p className="mt-1 text-xs text-slate-500">{tier.description}</p>
                <p className="mt-3">
                  <span className="text-2xl font-bold text-slate-900">
                    ${Number.isFinite(price.totalDue) ? price.totalDue.toFixed(0) : '0'}
                  </span>
                  <span className="text-sm text-slate-500">/{tierCycle}</span>
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {tier.maxSeats || 'Unlimited'} seats, {tier.maxVehicles || 'Unlimited'} vehicles, {tier.maxDevices || 'Unlimited'} devices
                </p>
                {isSelected && (
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-emerald-600">
                    <Check className="h-3 w-3" /> Selected
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Quantity adjusters */}
      <div className="rounded-2xl border border-slate-200/60 bg-slate-50/50 p-6">
        <h3 className="mb-4 text-sm font-semibold text-slate-900">Adjust Quantities</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { label: 'Vehicles', key: 'vehicleCount' as const, value: plan.vehicleCount },
            { label: 'Devices', key: 'deviceCount' as const, value: plan.deviceCount },
            { label: 'Seats', key: 'seatCount' as const, value: plan.seatCount },
          ].map((q) => (
            <div key={q.key}>
              <label className="text-xs font-medium text-slate-600">{q.label}</label>
              <Input
                type="number"
                min={q.key === 'seatCount' ? 1 : 0}
                value={q.value}
                onChange={(e) =>
                  updatePlan({
                    [q.key]:
                      q.key === 'seatCount'
                        ? Math.max(1, parseInt(e.target.value) || 1)
                        : Math.max(0, parseInt(e.target.value) || 0),
                  })
                }
                className="mt-1"
              />
            </div>
          ))}
        </div>
      </div>

      {commercialPreview && selectedAmountPreview && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 text-sm text-slate-700">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-emerald-900">
                Recommended commercial package: {commercialPreview.plan.name}
              </p>
              <p className="mt-1 text-xs text-slate-600">
                {commercialPreview.billingCycle} billing • {commercialPreview.counts.seatCount} seats • {commercialPreview.counts.vehicleCount} vehicles • {commercialPreview.counts.deviceCount} devices
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-slate-500">First invoice</p>
              <p className="text-lg font-semibold text-emerald-900">
                {commercialPreview.plan.currency} {selectedAmountPreview.totalDue.toFixed(2)}
              </p>
            </div>
          </div>
          {commercialPreview.autoSelected && (
            <p className="mt-3 text-xs text-emerald-800">
              The smallest valid plan is auto-selected from the live billing catalog, but you can still choose a larger plan.
            </p>
          )}
          {commercialPreview.warnings.length > 0 && (
            <div className="mt-3 space-y-1 text-xs text-amber-700">
              {commercialPreview.warnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" size="lg" className="flex-1 rounded-full" onClick={prevStep}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button
          variant="accent"
          size="lg"
          className="flex-1 rounded-full"
          onClick={nextStep}
          disabled={loadingPlans || Boolean(planLoadError) || !commercialPreview}
        >
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ── Step 4: Branding & CMS ───────────────────────────────── */

export function BrandingStep() {
  const { branding, updateBranding, organization, nextStep, prevStep } = useSignupWizard();

  const handleLogoUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        updateBranding({ logoFile: file, logoPreview: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  }, [updateBranding]);

  const timezones = [
    'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Cairo',
    'Asia/Dubai', 'Asia/Riyadh', 'Europe/London', 'Europe/Paris',
    'Europe/Amsterdam', 'Asia/Kolkata', 'America/New_York', 'America/Los_Angeles',
    'Australia/Sydney', 'America/Sao_Paulo', 'UTC',
  ];

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <StepHeader title="Brand Your Platform" subtitle="Customize how your dashboard looks to your team." />

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Form */}
        <div className="space-y-6">
          {/* Logo Upload */}
          <div>
            <FieldLabel htmlFor="logo">Organization Logo</FieldLabel>
            <label htmlFor="logo" className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white/60 p-8 transition hover:border-emerald-300 hover:bg-emerald-50/30">
              {branding.logoPreview ? (
                <img src={branding.logoPreview} alt="Logo preview" className="h-20 w-20 rounded-xl object-contain" />
              ) : (
                <>
                  <Upload className="mb-2 h-8 w-8 text-slate-400" />
                  <p className="text-sm font-medium text-slate-600">Click to upload logo</p>
                  <p className="text-xs text-slate-400">PNG, JPG or SVG, max 2MB</p>
                </>
              )}
              <input id="logo" type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            </label>
          </div>

          {/* Color */}
          <div>
            <FieldLabel htmlFor="color">Brand Color</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {colorPresets.map((color) => (
                <button key={color} onClick={() => updateBranding({ primaryColor: color })} className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${branding.primaryColor === color ? 'border-slate-900 scale-110' : 'border-transparent'}`} style={{ backgroundColor: color }} />
              ))}
              <input type="color" value={branding.primaryColor} onChange={(e) => updateBranding({ primaryColor: e.target.value })} className="h-8 w-8 cursor-pointer rounded-full border-0 bg-transparent p-0" />
            </div>
          </div>

          {/* Tagline */}
          <div>
            <FieldLabel htmlFor="tagline">Tagline (optional)</FieldLabel>
            <Input id="tagline" value={branding.tagline} onChange={(e) => updateBranding({ tagline: e.target.value })} placeholder="Your fleet, your way" />
          </div>

          {/* Timezone */}
          <div>
            <FieldLabel htmlFor="timezone">Timezone</FieldLabel>
            <select id="timezone" value={branding.timezone} onChange={(e) => updateBranding({ timezone: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100">
              {timezones.map((tz) => (<option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>))}
            </select>
          </div>
        </div>

        {/* Live Preview */}
        <div className="rounded-2xl border border-slate-200/60 bg-white p-6 shadow-sm">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Dashboard Preview</p>
          <div className="rounded-xl border border-slate-100 bg-slate-50 overflow-hidden">
            {/* Nav bar preview */}
            <div className="flex items-center gap-3 px-4 py-3" style={{ backgroundColor: branding.primaryColor }}>
              {branding.logoPreview ? (
                <img src={branding.logoPreview} alt="" className="h-6 w-6 rounded object-contain bg-white/20" />
              ) : (
                <div className="h-6 w-6 rounded bg-white/20" />
              )}
              <span className="text-sm font-semibold text-white">{organization.name || 'Your Org'}</span>
            </div>
            {/* Content preview */}
            <div className="space-y-3 p-4">
              {branding.tagline && <p className="text-xs text-slate-500 italic">{branding.tagline}</p>}
              <div className="flex gap-2">
                <div className="h-16 flex-1 rounded-lg bg-white border border-slate-100" />
                <div className="h-16 flex-1 rounded-lg bg-white border border-slate-100" />
                <div className="h-16 flex-1 rounded-lg bg-white border border-slate-100" />
              </div>
              <div className="h-24 rounded-lg bg-white border border-slate-100" />
              <div className="flex gap-2">
                <div className="h-3 w-20 rounded bg-slate-200" />
                <div className="h-3 w-16 rounded" style={{ backgroundColor: branding.primaryColor, opacity: 0.3 }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" size="lg" className="flex-1 rounded-full" onClick={prevStep}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button variant="accent" size="lg" className="flex-1 rounded-full" onClick={nextStep}>
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ── Step 5: Service Configuration ────────────────────────── */

export function ServicesStep() {
  const { services, organization, updateServices, plan, nextStep, prevStep } = useSignupWizard();
  const [planCatalog, setPlanCatalog] = useState<BillingPlan[]>([]);
  const [planCatalogError, setPlanCatalogError] = useState<string | null>(null);
  const normalizedIndustry = normalizeIndustrySelection(organization.industry);
  const tenantPackage =
    organization.tenantPackage || resolveTenantPackage(normalizedIndustry);
  const requiredModuleSet = useMemo(
    () => new Set<string>(PACKAGE_REQUIRED_SERVICE_KEYS[tenantPackage]),
    [tenantPackage],
  );

  const icons: Record<string, typeof MapPin> = {
    map: MapPin, vehicles: Truck, fleet: Truck, devices: Cpu,
    alerts: Bell, reports: FileBarChart, billing: CreditCard, users: Users, cameras: Camera,
    triggers: Zap, settings: Settings2, ai: Brain, ubi: Shield,
    'insurance-telemetry': Shield,
    'insurance-crash-claims': Bell,
    'insurance-risk-portfolio': FileBarChart,
    'insurance-underwriting': Brain,
    'insurance-api-gateway': Cpu,
    'insurance-fraud-compliance': Lock,
    'insurance-leaderboard-incentive': Users,
    farmersTelemetry: MapPin,
  };

  useEffect(() => {
    const bundledServices = applyIndustryServiceBundle(
      services.enabledServices,
      normalizedIndustry,
    );
    if (!isSameServiceSelection(bundledServices, services.enabledServices)) {
      updateServices({ enabledServices: bundledServices });
    }
  }, [normalizedIndustry, services.enabledServices, updateServices]);

  useEffect(() => {
    let cancelled = false;
    const loadPlanCatalog = async () => {
      try {
        setPlanCatalogError(null);
        const data = await fetchPublicBillingPlans();
        if (!cancelled) {
          setPlanCatalog(data);
        }
      } catch (err) {
        if (!cancelled) {
          setPlanCatalog([]);
          setPlanCatalogError(
            err instanceof Error ? err.message : 'Unable to load billing plan access rules',
          );
        }
      }
    };
    void loadPlanCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  const commercialPreview = useMemo(() => {
    if (planCatalog.length === 0) return null;
    try {
      return resolveCommercialSelection({
        plans: planCatalog,
        requestedPlanId: plan.tier,
        billingCycle: plan.billingCycle,
        seatCount: plan.seatCount,
        vehicleCount: plan.vehicleCount,
        deviceCount: plan.deviceCount,
        industry: normalizedIndustry,
        tenantPackage,
      });
    } catch {
      return null;
    }
  }, [
    normalizedIndustry,
    plan.billingCycle,
    plan.deviceCount,
    plan.seatCount,
    plan.tier,
    plan.vehicleCount,
    planCatalog,
    tenantPackage,
  ]);

  useEffect(() => {
    if (!commercialPreview) return;
    const allowedServices = new Set(
      Object.entries(commercialPreview.enabledServices)
        .filter(([, enabled]) => enabled)
        .map(([serviceKey]) => serviceKey),
    );
    const trimmedServices = services.enabledServices.filter(
      (serviceKey) =>
        allowedServices.has(serviceKey) || requiredModuleSet.has(serviceKey),
    );
    const bundledServices = applyIndustryServiceBundle(
      trimmedServices,
      normalizedIndustry,
    );
    if (!isSameServiceSelection(bundledServices, services.enabledServices)) {
      updateServices({ enabledServices: bundledServices });
    }
  }, [
    commercialPreview,
    normalizedIndustry,
    requiredModuleSet,
    services.enabledServices,
    updateServices,
  ]);

  const isRequiredForIndustry = (moduleId: string) => {
    return requiredModuleSet.has(moduleId);
  };

  const toggleService = (id: string) => {
    if (isRequiredForIndustry(id)) {
      return;
    }
    const current = services.enabledServices;
    const updated = current.includes(id)
      ? current.filter((s) => s !== id)
      : [...current, id];
    updateServices({ enabledServices: updated });
  };

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <StepHeader
        title="Configure Services"
        subtitle="Choose which platform modules to enable for your organization."
      />

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Active Package Bundle
        </p>
        <p className="mt-1 text-sm font-semibold text-slate-900">
          {TENANT_PACKAGE_LABELS[tenantPackage]}
        </p>
        {commercialPreview ? (
          <p className="mt-2 text-xs text-slate-600">
            Billing plan access is synced from {commercialPreview.plan.name}.
          </p>
        ) : planCatalogError ? (
          <p className="mt-2 text-xs text-amber-700">
            Live billing plan access rules are temporarily unavailable. Final module eligibility will be re-checked during launch.
          </p>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {serviceModules.map((mod) => {
          const availableByTier = commercialPreview
            ? commercialPreview.enabledServices[mod.id] === true
            : true;
          const moduleIndustryPackages =
            (mod as { industryPackages?: readonly string[] }).industryPackages ?? [];
          const industryAllowed =
            moduleIndustryPackages.length === 0 ||
            moduleIndustryPackages.includes(tenantPackage);
          const requiredForIndustry = isRequiredForIndustry(mod.id);
          const available = availableByTier && industryAllowed;
          const enabled = services.enabledServices.includes(mod.id);
          const Icon = icons[mod.id] || Settings2;
          const unavailableLabel = !availableByTier
            ? commercialPreview
              ? 'Not in plan'
              : 'Billing check pending'
            : !industryAllowed
              ? moduleIndustryPackages.includes('insurance')
                ? 'Insurance only'
                : 'Farming only'
              : null;

          return (
            <button
              key={mod.id}
              onClick={() => available && !requiredForIndustry && toggleService(mod.id)}
              disabled={!available}
              className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-all ${
                !available
                  ? 'cursor-not-allowed border-slate-100 bg-slate-50 opacity-60'
                  : enabled
                    ? 'border-emerald-500 bg-emerald-50/50 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${enabled ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-900">{mod.label}</p>
                  {requiredForIndustry && (
                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0.5">
                      Required
                    </Badge>
                  )}
                  {!available && unavailableLabel && (
                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0.5">
                      {unavailableLabel}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500">{mod.description}</p>
              </div>
              {available && (
                <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${enabled ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300'}`}>
                  {enabled && <Check className="h-3 w-3" />}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex gap-3">
        <Button variant="outline" size="lg" className="flex-1 rounded-full" onClick={prevStep}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button variant="accent" size="lg" className="flex-1 rounded-full" onClick={nextStep}>
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ── Step 6: Review & Launch ──────────────────────────────── */

export function ReviewStep({ onSubmit }: { onSubmit: () => void }) {
  const store = useSignupWizard();
  const { account, organization, plan, branding, services, agreedToTerms, setAgreedToTerms, isSubmitting, submitError, setStep, prevStep } = store;

  const [planCatalog, setPlanCatalog] = useState<BillingPlan[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadCatalog = async () => {
      try {
        const data = await fetchPublicBillingPlans();
        if (!cancelled) {
          setPlanCatalog(data);
        }
      } catch {
        // Non-fatal: fall back to local labels.
      }
    };
    void loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  const commercialPreview = useMemo(() => {
    if (planCatalog.length === 0) return null;
    try {
      return resolveCommercialSelection({
        plans: planCatalog,
        requestedPlanId: plan.tier,
        billingCycle: plan.billingCycle,
        seatCount: plan.seatCount,
        vehicleCount: plan.vehicleCount,
        deviceCount: plan.deviceCount,
        industry: organization.industry,
        tenantPackage: organization.tenantPackage,
      });
    } catch {
      return null;
    }
  }, [
    organization.industry,
    organization.tenantPackage,
    plan.billingCycle,
    plan.deviceCount,
    plan.seatCount,
    plan.tier,
    plan.vehicleCount,
    planCatalog,
  ]);

  const invoicePreview = useMemo(() => {
    if (!commercialPreview) return null;
    return estimateCommercialCycleAmount(
      commercialPreview.plan,
      commercialPreview.billingCycle,
      commercialPreview.counts,
    );
  }, [commercialPreview]);

  const tierLabel =
    commercialPreview?.plan.name ??
    planCatalog.find((p) => p.id === plan.tier)?.name ??
    pricingTiers.find((t) => t.tier === plan.tier)?.name ??
    plan.tier;

  const sections = [
    {
      title: 'Account',
      step: 0,
      items: [
        `${account.firstName} ${account.lastName}`,
        account.email,
        account.phone || 'No phone',
      ],
    },
    {
      title: 'Organization',
      step: 1,
      items: [
        organization.name,
        industries.find((i) => i.value === organization.industry)?.label ?? organization.industry,
        TENANT_PACKAGE_LABELS[organization.tenantPackage],
        countries.find((c) => c.value === organization.country)?.label ?? organization.country,
        fleetSizes.find((f) => f.value === organization.fleetSize)?.label ?? organization.fleetSize,
      ],
    },
    {
      title: 'Subscription',
      step: 2,
      items: [
        `${tierLabel} plan (${commercialPreview?.billingCycle ?? plan.billingCycle})`,
        `${commercialPreview?.counts.vehicleCount ?? plan.vehicleCount} vehicles, ${commercialPreview?.counts.deviceCount ?? plan.deviceCount} devices, ${commercialPreview?.counts.seatCount ?? plan.seatCount} seats`,
        invoicePreview
          ? `Estimated first invoice: ${commercialPreview?.plan.currency} ${invoicePreview.totalDue.toFixed(2)}`
          : 'Estimated first invoice will be calculated from the billing catalog at launch',
      ],
    },
    {
      title: 'Branding',
      step: 3,
      items: [
        branding.logoPreview ? 'Logo uploaded' : 'No logo',
        `Color: ${branding.primaryColor}`,
        branding.tagline || 'No tagline',
        `Timezone: ${branding.timezone.replace(/_/g, ' ')}`,
      ],
    },
    {
      title: 'Services',
      step: 4,
      items: [`${services.enabledServices.length} modules enabled`],
    },
  ];

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <StepHeader title="Review & Launch" subtitle="Almost there! Review your setup and launch your fleet platform." />

      <div className="space-y-4">
        {sections.map((section) => (
          <div key={section.title} className="rounded-2xl border border-slate-200/60 bg-white p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">{section.title}</h3>
              <button onClick={() => setStep(section.step)} className="text-xs font-medium text-emerald-600 hover:text-emerald-700">
                Edit
              </button>
            </div>
            <div className="space-y-1">
              {section.items.map((item, i) => (
                <p key={i} className="text-sm text-slate-600">{item}</p>
              ))}
            </div>
          </div>
        ))}
      </div>

      {commercialPreview?.warnings.length ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900">
          {commercialPreview.warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      ) : null}

      {/* Terms */}
      <div className="rounded-2xl border border-slate-200/60 bg-slate-50/50 p-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-400" />
          <span className="text-sm text-slate-600">
            I agree to the{' '}
            <a href="/terms" target="_blank" className="font-medium text-emerald-600 underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy" target="_blank" className="font-medium text-emerald-600 underline">Privacy Policy</a>.
          </span>
        </label>
      </div>

      {submitError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {submitError}
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" size="lg" className="flex-1 rounded-full" onClick={prevStep}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button variant="accent" size="lg" className="flex-1 rounded-full" onClick={onSubmit} disabled={!agreedToTerms || isSubmitting}>
          {isSubmitting ? (
            <><span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" /> Launching...</>
          ) : (
            <><Rocket className="h-4 w-4" /> Launch My Fleet Platform</>
          )}
        </Button>
      </div>
    </div>
  );
}
